import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import UploadFileModal from '@nxsys/components/common/UploadFileModal';
import { useImportTimesheet } from '@nxsys/hooks/api/import';
import useImportSocket from '@nxsys/hooks/useImportSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { importAction } from '@nxsys/store/slices/import/reducer/importSlice';
import { GetImportTimesheetListParams } from '@nxsys/store/slices/import/types';
import { showMessage } from '@nxsys/utils/message';
import { debounce } from 'lodash';
import {
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ViewImportedTimesheetFile from '../components/ViewImportedTimesheetFile';
import { getImportTimesheetColumns } from './columns';
import ImportTimesheetResultUploadModal from '../components/ImportTimesheetResultUploadModal';

const ImportTimesheetTab = ({
  importFileRef,
}: {
  importFileRef: Ref<{ open: () => void }>;
}) => {
  const { messages } = useTypedIntl();

  const { user, isBureauEmployer, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { importTimesheetList } = useSelector(
    (state: AppState) => state.importStore,
  );
  const { loading, list, pagination, queryParams } = importTimesheetList;

  const dispatch = useDispatch<AppDispatch>();

  const [searchValue, setSearchValue] = useState('');
  const [openUploadFileModal, setOpenUploadFileModal] = useState(false);
  const [openResultUploadModal, setOpenResultUploadModal] = useState(false);
  const [dataImported, setDataImported] = useState<IImportTimesheet | null>(
    null,
  );
  const [openViewImportedFileModal, setOpenViewImportedFileModal] =
    useState(false);

  useImperativeHandle(importFileRef, () => ({
    open: () => {
      setOpenUploadFileModal(true);
    },
  }));

  const { run: importTimesheet, loading: importTimesheetLoading } =
    useImportTimesheet();

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetImportTimesheetListParams) => {
    dispatch(
      importAction.getImportTimesheetList({
        authorId: isBureauEmployer ? user?.id : undefined,
        ...params,
      }),
    );
  };

  const refetchData = () =>
    handleFetchData({
      ...queryParams,
    });

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const handleUploaded = useCallback(
    async (fileData: { url: string; fileName: string; fileSize: number }) => {
      const params = {
        ...fileData,
        ...(isBureau ? { isPDF: false } : {}),
      };
      setOpenResultUploadModal(true);
      const res = await importTimesheet(params);
      setDataImported(res.data);
    },
    [],
  );

  const handleViewImportedFile = useCallback((data: IImportTimesheet) => {
    setDataImported(data);
    setOpenViewImportedFileModal(true);
  }, []);

  useImportSocket({
    eventName: `import-timesheet-process-notification-${user?.id}`,
    onSuccess: (data) => {
      if (data?.success) {
        showMessage({
          type: 'success',
          content: messages['import.timesheet.importSuccess'],
        });
        refetchData();
      }
    },
  });

  return (
    <>
      <div className="ImportTimesheetTab">
        <AppsHeader>
          <AppInputSearch value={searchValue} onChange={handleSearchChange} />
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            data={
              list?.map((item, index) => ({
                ...item,
                indexNumber:
                  pagination.current * pagination.pageSize + index + 1,
              })) ?? []
            }
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1000, y: 700 }}
            columns={getImportTimesheetColumns({
              messages,
              onView: handleViewImportedFile,
            })}
            onChangePagination={onChangePagination}
            noDataType={!!queryParams?.search ? 'noDataFound' : 'noDataCreated'}
            addNewBtnName={messages['common.importFile']}
            addNewBtnOnClick={() => setOpenUploadFileModal(true)}
            addNewIcon={null}
          />
        </AppsContent>
      </div>
      <UploadFileModal
        open={openUploadFileModal}
        onClose={() => setOpenUploadFileModal(false)}
        onUploaded={handleUploaded}
        fileType="spreadsheet"
      />
      <ImportTimesheetResultUploadModal
        isOpen={openResultUploadModal}
        toggle={() => {
          setOpenResultUploadModal(!openResultUploadModal);
          setDataImported(null);
        }}
        resultData={dataImported as any}
        loading={importTimesheetLoading}
      />
      <ViewImportedTimesheetFile
        isOpen={openViewImportedFileModal}
        toggle={() => setOpenViewImportedFileModal(!openViewImportedFileModal)}
        id={dataImported?.id}
      />
    </>
  );
};

export default ImportTimesheetTab;
